import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bug, Copy, Check, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  getErrors,
  clearErrors,
  subscribeToErrors,
  type ErrorEntry,
  type ErrorType,
} from "../../lib/errorInterceptor";
import { useAuth } from "../../contexts/AuthContext";

const ALLOWED_EMAIL = "avivhdr@gmail.com";

const TYPE_CONFIG: Record<
  ErrorType,
  { label: string; color: string; bg: string }
> = {
  network: {
    label: "Network",
    color: "text-warm-text-primary",
    bg: "bg-warm-bg-elevated",
  },
  "console.error": {
    label: "Error",
    color: "text-red-700 dark:text-red-300",
    bg: "bg-red-100 dark:bg-red-900/40",
  },
  "console.warn": {
    label: "Warning",
    color: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-100 dark:bg-amber-900/40",
  },
  "unhandled-rejection": {
    label: "Rejection",
    color: "text-red-700 dark:text-red-300",
    bg: "bg-red-100 dark:bg-red-900/40",
  },
  "global-error": {
    label: "Error",
    color: "text-red-700 dark:text-red-300",
    bg: "bg-red-100 dark:bg-red-900/40",
  },
};

// Categories for multi-select filter chips
type FilterCategory = "network" | "errors" | "warnings";

function entryCategory(entry: ErrorEntry): FilterCategory {
  if (entry.type === "network") return "network";
  if (entry.type === "console.warn") return "warnings";
  return "errors";
}

function isWarningOnly(entries: ErrorEntry[]): boolean {
  return entries.length > 0 && entries.every((e) => e.type === "console.warn");
}

function relativeTime(timestamp: number): string {
  const diff = Math.floor((Date.now() - timestamp) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const DEFAULT_FILTERS = new Set<FilterCategory>(["network", "errors"]);

const FILTER_CHIPS: { key: FilterCategory; label: string }[] = [
  { key: "network", label: "Network" },
  { key: "errors", label: "Errors" },
  { key: "warnings", label: "Warnings" },
];

const NetworkErrorLog: React.FC = () => {
  const { user } = useAuth();
  const [errors, setErrors] = useState<ErrorEntry[]>(getErrors);
  const [open, setOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] =
    useState<Set<FilterCategory>>(DEFAULT_FILTERS);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return subscribeToErrors(() => {
      const allErrors = getErrors();
      setErrors(allErrors);
      // Auto-open only when there are actual errors (not warnings-only)
      if (!isWarningOnly(allErrors)) {
        setOpen(true);
      }
    });
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const copyToClipboard = useCallback(async (text: string, id?: string) => {
    await navigator.clipboard.writeText(text);
    if (id) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } else {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    }
  }, []);

  const filtered = errors.filter((e) =>
    selectedFilters.has(entryCategory(e)),
  );

  const handleCopyAll = useCallback(() => {
    const text = filtered
      .map(
        (e) =>
          `---\n\n${e.fullReport}\n\n**Timestamp:** ${new Date(e.timestamp).toISOString()}`,
      )
      .join("\n\n");
    copyToClipboard(text);
  }, [filtered, copyToClipboard]);

  const handleClear = useCallback(() => {
    clearErrors();
    setErrors([]);
    setExpandedId(null);
  }, []);

  const toggleFilter = useCallback((key: FilterCategory) => {
    setSelectedFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  if (!import.meta.env.DEV && user?.email !== ALLOWED_EMAIL) return null;
  if (errors.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[9999]" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-3.5 bg-warm-bg-elevated text-warm-text-secondary hover:bg-warm-border-subtle rounded-full shadow-lg transition-all"
        aria-label={`Error log, ${errors.length} entries`}
      >
        <Bug size={24} />
        {errors.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
            {errors.length > 99 ? "99+" : errors.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 bottom-full mb-2 w-[500px] max-w-[calc(100vw-2rem)] max-h-[600px] bg-warm-bg-card border border-warm-border rounded-lg shadow-lg flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-warm-border">
            <h3 className="text-sm font-semibold text-warm-text-primary">
              Error Log
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyAll}
                disabled={filtered.length === 0}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-warm-text-secondary hover:text-warm-text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {copiedAll ? (
                  <Check size={12} className="text-green-500" />
                ) : (
                  <Copy size={12} />
                )}
                {copiedAll ? "Copied!" : "Copy All"}
              </button>
              <button
                onClick={handleClear}
                disabled={errors.length === 0}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-warm-text-secondary hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={12} />
                Clear
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-warm-text-secondary hover:text-warm-text-primary transition-colors"
                aria-label="Close error log"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Filter chips (multi-select) */}
          <div className="flex gap-1.5 px-4 py-2 border-b border-warm-border">
            {FILTER_CHIPS.map((chip) => {
              const active = selectedFilters.has(chip.key);
              return (
                <button
                  key={chip.key}
                  onClick={() => toggleFilter(chip.key)}
                  aria-pressed={active}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                    active
                      ? "bg-warm-blue border-warm-blue text-white"
                      : "bg-transparent border-warm-border text-warm-text-secondary hover:border-warm-text-secondary"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>

          {/* Entry list */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-sm text-warm-text-secondary">
                No entries match the selected filters
              </div>
            ) : (
              <div className="divide-y divide-warm-border">
                {[...filtered].reverse().map((entry) => {
                  const config = TYPE_CONFIG[entry.type];
                  const isExpanded = expandedId === entry.id;
                  return (
                    <div
                      key={entry.id}
                      className="px-4 py-3 hover:bg-warm-bg-elevated/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          onClick={() => toggleExpanded(entry.id)}
                          className="flex-1 min-w-0 text-left"
                          aria-expanded={isExpanded}
                          aria-label={
                            isExpanded
                              ? "Collapse error details"
                              : "Expand error details"
                          }
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${config.bg} ${config.color}`}
                            >
                              {config.label}
                            </span>
                            <span className="text-[11px] text-warm-text-secondary">
                              {relativeTime(entry.timestamp)}
                            </span>
                          </div>
                          <p className="text-xs text-warm-text-primary break-words">
                            {entry.summary}
                          </p>
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => toggleExpanded(entry.id)}
                            className="p-1 text-warm-text-secondary hover:text-warm-text-primary transition-colors"
                            aria-label={
                              isExpanded
                                ? "Collapse error details"
                                : "Expand error details"
                            }
                          >
                            {isExpanded ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </button>
                          <button
                            onClick={() =>
                              copyToClipboard(entry.fullReport, entry.id)
                            }
                            className="p-1 text-warm-text-secondary hover:text-warm-text-primary transition-colors"
                            aria-label="Copy full report"
                          >
                            {copiedId === entry.id ? (
                              <Check size={14} className="text-green-500" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <pre className="mt-2 p-2 text-[11px] text-warm-text-primary bg-warm-bg-elevated rounded border border-warm-border overflow-x-auto whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                          {entry.fullReport}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkErrorLog;
